// pages/legal/terms-of-service.page.jsx
import PageBackdrop from "../../components/ui/PageBackdrop";

const BUSINESS_NAME =
  process.env.REACT_APP_BUSINESS_NAME || "Cascade Collective LLC";
const APP_NAME = process.env.REACT_APP_NAME || "Max Stock Machine";
const CONTACT_EMAIL =
  process.env.REACT_APP_CONTACT_EMAIL || "mrzimmer77@gmail.com";
const APP_URL = process.env.REACT_APP_URL || "https://maxstockmachine.com";
const EFFECTIVE_DATE = "April 12, 2026";

export const TOS_TEXT = `TERMS OF SERVICE

Effective Date: ${EFFECTIVE_DATE}
Operated by: ${BUSINESS_NAME} ("Company", "we", "us", "our")

PLEASE READ THESE TERMS CAREFULLY. BY ACCESSING OR USING ${APP_NAME.toUpperCase()}, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, DO NOT USE THE SERVICE.

1. ACCEPTANCE OF TERMS

By creating an account or using ${APP_NAME} ("Service"), you agree to these Terms of Service and our Privacy Policy. These Terms constitute a legally binding agreement between you and ${BUSINESS_NAME}.

2. DESCRIPTION OF SERVICE

${APP_NAME} is a stock research and analysis platform that provides quantitative valuation models, qualitative business ratings, watchlist alerts, and related financial data tools. The Service is provided for informational and educational purposes only.

3. NOT FINANCIAL ADVICE — LIMITATION OF LIABILITY

THE CONTENT, ANALYSIS, RATINGS, VALUATIONS, CONVICTION SCORES, AND ALL OTHER INFORMATION PROVIDED BY ${APP_NAME.toUpperCase()} IS FOR INFORMATIONAL AND EDUCATIONAL PURPOSES ONLY AND DOES NOT CONSTITUTE FINANCIAL, INVESTMENT, LEGAL, OR TAX ADVICE.

${BUSINESS_NAME}, its owners, operators, employees, agents, and affiliates SHALL NOT BE LIABLE FOR ANY LOSSES, DAMAGES, COSTS, OR EXPENSES OF ANY KIND, WHETHER DIRECT, INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY, arising from or related to:

(a) Your use of or reliance on any information provided by the Service;
(b) Any investment decisions made based on content from the Service;
(c) Any errors, inaccuracies, or omissions in the data or analysis;
(d) Any interruption, suspension, or termination of the Service;
(e) Any unauthorized access to or alteration of your data.

YOU EXPRESSLY AGREE THAT YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. YOU ARE SOLELY RESPONSIBLE FOR ANY INVESTMENT DECISIONS YOU MAKE. PAST PERFORMANCE OF ANY STOCK OR ANALYSIS IS NOT INDICATIVE OF FUTURE RESULTS.

TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL ${BUSINESS_NAME.toUpperCase()} BE LIABLE TO YOU FOR ANY AMOUNT EXCEEDING THE TOTAL FEES PAID BY YOU TO ${BUSINESS_NAME} IN THE THREE (3) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.

4. NO WARRANTY

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.

5. SUBSCRIPTION AND PAYMENT

5.1 Subscription Fee. Access to ${APP_NAME} requires a paid subscription of $20.00 USD per month ("Subscription Fee"). All fees are billed in advance on a monthly basis.

5.2 Payment Processing. All payments are processed through Stripe, a third-party payment processor. By providing payment information, you authorize ${BUSINESS_NAME} to charge your payment method for the Subscription Fee on a recurring monthly basis.

5.3 Automatic Renewal. Your subscription will automatically renew each month unless cancelled. You authorize us to charge your payment method for each renewal period.

5.4 Cancellation. You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. No partial refunds are issued for unused portions of a billing period.

5.5 Refunds. All subscription fees are non-refundable except where required by applicable law. We reserve the right to issue refunds at our sole discretion.

5.6 Price Changes. We reserve the right to change our pricing at any time. We will provide at least 30 days notice of any price changes via email.

5.7 Failed Payments. If a payment fails, we reserve the right to suspend or terminate your access to the Service.

6. ACCOUNT REGISTRATION

You must create an account to use the Service. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately at ${CONTACT_EMAIL} of any unauthorized use of your account.

7. ACCEPTABLE USE

You agree not to:
(a) Use the Service for any unlawful purpose or in violation of any applicable laws;
(b) Redistribute, resell, or commercialize any content from the Service without written permission;
(c) Scrape, crawl, or systematically extract data from the Service;
(d) Attempt to gain unauthorized access to any part of the Service;
(e) Use the Service to transmit spam, malware, or harmful content;
(f) Impersonate any person or entity;
(g) Interfere with or disrupt the integrity or performance of the Service.

8. INTELLECTUAL PROPERTY

All content, features, and functionality of ${APP_NAME}, including but not limited to text, graphics, data, analysis, code, and designs, are the exclusive property of ${BUSINESS_NAME} and are protected by applicable intellectual property laws. You are granted a limited, non-exclusive, non-transferable license to access and use the Service for your personal, non-commercial use only.

9. THIRD-PARTY DATA

The Service incorporates financial data from third-party providers. We do not warrant the accuracy, completeness, or timeliness of such data. Third-party data providers have their own terms of service which may apply to your use of such data.

10. INDEMNIFICATION

You agree to defend, indemnify, and hold harmless ${BUSINESS_NAME} and its owners, officers, agents, and employees from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising out of or related to your use of the Service, your violation of these Terms, or your violation of any rights of another party.

11. TERMINATION

We reserve the right to suspend or terminate your account and access to the Service at any time, with or without cause, and with or without notice. Upon termination, your right to use the Service will immediately cease.

12. GOVERNING LAW AND DISPUTE RESOLUTION

These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in Texas.

13. SEVERABILITY

If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will otherwise remain in full force and effect.

14. ENTIRE AGREEMENT

These Terms, together with our Privacy Policy, constitute the entire agreement between you and ${BUSINESS_NAME} regarding the Service and supersede all prior agreements and understandings.

15. CONTACT

For questions about these Terms, contact us at:
${BUSINESS_NAME}
Email: ${CONTACT_EMAIL}
Website: ${APP_URL}`;

const TermsOfServicePage = () => (
  <>
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      .lp-root { background: var(--bg); min-height: 100vh; font-family: var(--font-sans); color: var(--text-primary); position: relative; }
      .lp-inner { position: relative; z-index: 1; max-width: 720px; margin: 0 auto; padding: 52px 24px 80px; }
      .lp-eyebrow { font-family: var(--font-mono); font-size: 12px; color: var(--accent); letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 12px; }
      .lp-title { font-family: var(--font-display); font-size: clamp(28px, 4vw, 40px); font-weight: 400; line-height: 1.1; letter-spacing: -0.02em; margin-bottom: 8px; }
      .lp-effective { font-family: var(--font-mono); font-size: 12px; color: var(--text-tertiary); margin-bottom: 40px; }
      .lp-body { background: var(--surface-1); border: 1px solid var(--border-subtle); border-radius: var(--radius-md); padding: 28px; }
      .lp-pre { font-family: var(--font-mono); font-size: 13px; color: var(--text-tertiary); line-height: 1.9; white-space: pre-wrap; word-break: break-word; }
      .nf-link { font-family: var(--font-mono); font-size: 12px; color: var(--accent); text-decoration: none; border: 1px solid var(--accent-border); padding: 8px 20px; border-radius: var(--radius-sm); margin-top: 8px; transition: background var(--dur-fast) var(--ease-out); }
      .nf-link:hover { background: var(--accent-soft); }
      @media (max-width: 500px) { .lp-inner { padding: 32px 16px 60px; } .lp-body { padding: 18px; } }
    `}</style>
    <div className="lp-root">
      <PageBackdrop />
      <div className="lp-inner">
        <p className="lp-eyebrow">Legal</p>
        <h1 className="lp-title">Terms of Service</h1>
        <p className="lp-effective">
          Effective {EFFECTIVE_DATE} · {BUSINESS_NAME}
        </p>
        <div className="lp-body">
          <pre className="lp-pre">{TOS_TEXT}</pre>
        </div>
      </div>
    </div>
  </>
);

export default TermsOfServicePage;
